import { useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Divider } from '@hivebrite/design-system/Divider';
import {
  getReactSelectValue,
  ReactSelectInput,
} from '@hivebrite/design-system/Inputs';

import type { ContentItemProps } from 'hb-builder/components/BuilderComponents/block/Strips/types/props';
import { stripImageRecommendedSize } from 'hb-builder/components/BuilderComponents/constants/imageSizes';
import DeleteItemModal from 'hb-builder/components/common/modal/DeleteItemModal';
import ButtonLink from 'hb-builder/components/common/SettingsLibrary/CommonSettings/ButtonLink';
import CheckboxSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/CheckboxSetting';
import DeleteImageSource from 'hb-builder/components/common/SettingsLibrary/CommonSettings/DeleteImageSource';
import DisplayFieldOption from 'hb-builder/components/common/SettingsLibrary/CommonSettings/DisplayFieldOption';
import DisplayOption from 'hb-builder/components/common/SettingsLibrary/CommonSettings/DisplayOption';
import EditImageSource from 'hb-builder/components/common/SettingsLibrary/CommonSettings/EditImageSource';
import TextSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/TextSetting';
import InputTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/InputTitle';
import SettingBlock from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/SettingBlock';

const positionOptions = [
  {
    value: 'left',
    label: __('Left'),
  },
  {
    value: 'right',
    label: __('Right'),
  },
  {
    value: 'top',
    label: __('Top'),
  },
  {
    value: 'bottom',
    label: __('Bottom'),
  },
  {
    value: 'center',
    label: __('Center'),
  },
  {
    value: 'top left',
    label: __('Top Left'),
  },
  {
    value: 'top right',
    label: __('Top Right'),
  },
  {
    value: 'bottom left',
    label: __('Bottom Left'),
  },
  {
    value: 'bottom right',
    label: __('Bottom Right'),
  },
];

const MIN_FRAME = 1;

const ContentItem = ({
  componentName,
  onChange,
  item: stripsItem,
  parentItem,
  removeItemOpen,
}: ContentItemProps) => {
  const [image, headline, subline, button] = stripsItem.children;

  const [positionValue, setPositionValue] = useState(
    image.attributes?.position || 'center',
  );

  const positionValueSelected = getReactSelectValue(
    positionOptions,
    positionValue,
  );

  const canDeleteItem = (parentItem.children?.length || 0) > MIN_FRAME;

  const handleChangePosition = (e) => {
    setPositionValue(e.value);

    onChange({
      id: image.id,
      settingKey: 'attributes.position',
      value: e.value,
    });
  };

  return (
    <SettingBlock boxProps={{ mt: 1, mb: 4 }}>
      {/* IMAGE */}
      <InputTitle label={__('Image')} />
      <Box my={2}>
        {s__(
          'Builder|Recommended %{size}px. The file must not exceed 2.5 MB',
        ).i__({
          size: stripImageRecommendedSize[stripsItem.template!],
        })}
      </Box>
      <EditImageSource hasImage={!!image.attributes?.url} id={image.id} />
      <DeleteImageSource
        settingKey="attributes.url"
        item={image}
        onChange={onChange}
      />
      <ImageSettingWrapper>
        <InputTitle id="settingsImagePosition" label={__('Image position')} />
        <ReactSelectInput
          id="heroSettingsImagePosition"
          name="heroSettingsImagePosition"
          onChange={handleChangePosition}
          options={positionOptions}
          value={positionValueSelected}
          width={155}
        />
      </ImageSettingWrapper>
      <DisplayFieldOption
        label={s__('Builder|Headline & image clickable')}
        onChange={onChange}
        item={image}
        keySetter="attributes.clickable"
      />
      <InputTitle label={__('Alternative text')} />
      <TextSetting
        item={image}
        onChange={onChange}
        placeholder={__('Enter alternative text')}
        description={__("This will appear if the image can't be displayed")}
        settingKey="attributes.alt"
        width="100%"
      />

      <Divider mt={2} />

      <InputTitle label={__('Text')} boxProps={{ mb: 4 }} />

      {/* HEADLINE */}
      <InputTitle
        boxProps={{ mb: 3 }}
        label={__('Headline (max characters: 50)')}
      />
      <TextSetting
        item={headline}
        maxLength={50}
        onChange={onChange}
        placeholder={__('Enter a headline')}
        settingKey="content"
        width="100%"
      />

      {/* SUBLINE */}
      <InputTitle
        boxProps={{ mb: 3 }}
        label={__('Subline (max characters: 340)')}
      />
      <TextSetting
        item={subline}
        maxLength={340}
        onChange={onChange}
        placeholder={__('Enter a subline')}
        settingKey="content"
        width="100%"
      />
      <Divider mt={2} />
      {/* BUTTON */}
      <InputTitle boxProps={{ mb: 4 }} label={__('Button')} />
      <DisplayOption
        title={__('Display button')}
        item={button}
        onChange={onChange}
      />
      <TextSetting
        item={button}
        onChange={onChange}
        placeholder={__('Content')}
        settingKey="content"
        width="100%"
      />
      <ButtonLink item={button} onChange={onChange} placeholder={__('Link')} />
      <CheckboxSetting
        item={button}
        onChange={onChange}
        settingKey="buttonLinkNewPage"
        title={s__('Builder|Open link in a new tab')}
      />

      <DeleteItemModal
        id={stripsItem.id}
        itemName={componentName}
        onConfirm={() => removeItemOpen(stripsItem.id)}
        isDisabled={!canDeleteItem}
      />
    </SettingBlock>
  );
};

export default ContentItem;

const ImageSettingWrapper = styled(Box).attrs(() => ({
  alignItems: 'center',
  justifyContent: 'space-between',
  mb: 4,
}))``;
